// Port of ios/Sources/ViewModels/ComposeFormatting.swift.
//
// The formatting toolbar's text surgery: wrapping (and unwrapping) a selection in Markdown
// delimiters. Android's `ComposeFormatting`, rule for rule, pure over (text, selection) so the
// interesting cases — unwrap, empty selection, whitespace-edged selections — live in tests.
//
// Offsets are UTF-16 code units (what `NSTextView.selectedRange()` speaks — and what a
// `<textarea>`'s `selectionStart`/`selectionEnd` speak), so JS string indices map one to one.

export type Format = 'bold' | 'italic' | 'strikethrough' | 'code';

export const allFormats: readonly Format[] = ['bold', 'italic', 'strikethrough', 'code'];

export function delimiter(format: Format): string {
  switch (format) {
    case 'bold':
      return '**';
    case 'italic':
      return '_';
    case 'strikethrough':
      return '~~';
    case 'code':
      return '`';
  }
}

export interface Applied {
  text: string;
  selectionStart: number;
  selectionEnd: number;
}

const WHITESPACE = /\s/u;

/**
 * Applies `format` to the selection:
 * - a selection already wrapped in the delimiter is UNWRAPPED — the button toggles, because
 *   a toggle is what the bold button has meant in every editor since 1985;
 * - an unwrapped selection is wrapped, with edge whitespace moved OUTSIDE the delimiters
 *   (`** hola **` does not parse as bold in Markdown — `**hola**` does);
 * - an empty selection inserts the delimiter pair and parks the cursor inside it.
 *
 * The returned selection always covers the (un)wrapped inner text, so repeated presses
 * toggle cleanly instead of nesting.
 */
export function toggle(text: string, selectionStart: number, selectionEnd: number, format: Format): Applied {
  const start = Math.max(0, Math.min(selectionStart, text.length));
  const end = Math.max(start, Math.min(selectionEnd, text.length));
  const delim = delimiter(format);
  const delimiterLength = delim.length;

  if (start === end) {
    const inserted = text.slice(0, start) + delim + delim + text.slice(start);
    const cursor = start + delimiterLength;
    return { text: inserted, selectionStart: cursor, selectionEnd: cursor };
  }

  const selected = text.slice(start, end);

  // Unwrap when the delimiters sit just OUTSIDE the selection…
  const outsideStart = start - delimiterLength;
  const outsideEnd = end + delimiterLength;
  if (
    outsideStart >= 0 &&
    outsideEnd <= text.length &&
    text.slice(outsideStart, start) === delim &&
    text.slice(end, outsideEnd) === delim
  ) {
    const newText = text.slice(0, outsideStart) + selected + text.slice(outsideEnd);
    return { text: newText, selectionStart: outsideStart, selectionEnd: outsideStart + selected.length };
  }
  // …or just INSIDE it (the user selected the delimiters too).
  if (selected.length >= delimiterLength * 2 && selected.startsWith(delim) && selected.endsWith(delim)) {
    const inner = selected.slice(delimiterLength, selected.length - delimiterLength);
    const newText = text.slice(0, start) + inner + text.slice(end);
    return { text: newText, selectionStart: start, selectionEnd: start + inner.length };
  }

  // Wrap, keeping edge whitespace outside the delimiters: `** hola **` is not bold.
  // Pure-whitespace selections are a no-op FIRST — otherwise leading and trailing both
  // consume the entire selection and the arithmetic below inverts its bounds.
  if (selected.trim() === '') {
    return { text, selectionStart: start, selectionEnd: end };
  }
  let leadingLength = 0;
  while (leadingLength < selected.length && WHITESPACE.test(selected[leadingLength])) leadingLength++;
  let trailingLength = 0;
  while (trailingLength < selected.length && WHITESPACE.test(selected[selected.length - 1 - trailingLength])) {
    trailingLength++;
  }
  const leading = selected.slice(0, leadingLength);
  const trailing = selected.slice(selected.length - trailingLength);
  const core = selected.slice(leadingLength, selected.length - trailingLength);
  const wrapped = leading + delim + core + delim + trailing;
  const newText = text.slice(0, start) + wrapped + text.slice(end);
  const innerStart = start + leadingLength + delimiterLength;
  return { text: newText, selectionStart: innerStart, selectionEnd: innerStart + core.length };
}
